/**
 * Popular Designs — Client-side (formerly "Community Feed")
 *
 * Fetches admin-curated Popular Designs and renders them below the ring designer.
 * Each card has two actions:
 *   - "Customize" — loads the design into the designer above (legacy behavior)
 *   - "Add to Cart" — opens an in-card size picker and adds directly to cart
 *
 * Sort/upvote/share controls are gone — admin curates the list and order.
 */

(function () {
  'use strict';

  const LIMIT = 20;
  let feedUrl = '';
  let offset = 0;
  let hasMore = false;
  let loading = false;

  // Ring sizes available — matches the variant map's size axis.
  const RING_SIZES = ['4','4.5','5','5.5','6','6.5','7','7.5','8','8.5','9','9.5','10','10.5','11','11.5','12','12.5','13','13.5','14','14.5','15'];

  function init(url) {
    feedUrl = url;
    const container = document.getElementById('community-feed');
    if (!container) return;
    loadFeed(container);
  }

  async function loadFeed(container) {
    if (loading) return;
    loading = true;

    const grid = container.querySelector('.cf-grid');
    const footer = container.querySelector('.cf-footer');
    const loadingEl = container.querySelector('.cf-loading');
    const emptyEl = container.querySelector('.cf-empty');

    if (offset === 0 && loadingEl) loadingEl.style.display = 'block';
    if (emptyEl) emptyEl.style.display = 'none';

    try {
      const res = await fetch(`${feedUrl}?limit=${LIMIT}&offset=${offset}`);
      const data = await res.json();

      if (loadingEl) loadingEl.style.display = 'none';

      const posts = data.posts || [];
      hasMore = data.hasMore || false;

      if (posts.length === 0 && offset === 0) {
        if (emptyEl) {
          emptyEl.textContent = 'No popular designs yet — check back soon.';
          emptyEl.style.display = 'block';
        }
        if (footer) footer.style.display = 'none';
        loading = false;
        return;
      }

      posts.forEach(post => {
        const card = createCard(post);
        grid.appendChild(card);
      });

      offset += posts.length;

      if (footer) {
        footer.style.display = hasMore ? 'block' : 'none';
      }
    } catch (e) {
      if (loadingEl) loadingEl.style.display = 'none';
      console.warn('[PopularDesigns] Load failed:', e);
    }

    loading = false;
  }

  function createCard(post) {
    const card = document.createElement('div');
    card.className = 'cf-card';
    card.setAttribute('data-design-id', post.id);

    const thumbHTML = post.thumbnail
      ? `<img src="${post.thumbnail}" alt="${escHtml(post.designName)}" loading="lazy">`
      : '<div style="color:#999;font-size:0.75rem;">No preview</div>';

    // pricesBySize: { '4': {tier, tierSlug, price, variantId, available, ...}, ... }
    // If present, use it for accurate per-size pricing AND to filter sizes that
    // would render with a corrupted layout (placement count dropped >25%).
    // Falls back to the design's stored tier (flat price) when pricesBySize is absent.
    const priceLabel = formatCardPriceLabel(post);

    // Build size <option> list — only sizes flagged available remain.
    const availableSizes = getAvailableSizes(post);
    const sizeOptions = availableSizes.length
      ? availableSizes.map(s => {
          const priceForSize = formatSizePrice(post, s);
          const label = priceForSize ? `Ring Size ${s} — ${priceForSize}` : `Ring Size ${s}`;
          return `<option value="${s}">${label}</option>`;
        }).join('')
      : RING_SIZES.map(s => `<option value="${s}">Ring Size ${s}</option>`).join(''); // pre-pricesBySize records

    card.innerHTML = `
      <div class="cf-card-thumb">${thumbHTML}</div>
      <div class="cf-card-body">
        <div class="cf-card-name">${escHtml(post.designName)}</div>
        ${priceLabel ? `<div class="cf-card-price">${priceLabel}</div>` : ''}
        <div class="cf-card-actions">
          <button class="cf-action-cart" data-id="${post.id}" title="Choose a ring size">Choose Size</button>
          <button class="cf-action-customize" data-id="${post.id}" title="Open in the designer to customize">✏️ Customize</button>
        </div>
        <div class="cf-size-picker" style="display:none;margin-top:8px;">
          <select class="cf-size-select" style="padding:6px 8px;border:1px solid #ccc;border-radius:4px;width:100%;margin-bottom:6px;">
            ${sizeOptions}
          </select>
          <button class="cf-confirm-add" style="background:#2a7;color:#fff;border:none;padding:8px;border-radius:4px;width:100%;font-weight:600;cursor:pointer;">Add to Cart</button>
          <button class="cf-cancel-add" style="background:transparent;color:#888;border:none;padding:4px;margin-top:4px;width:100%;font-size:0.78rem;cursor:pointer;">Cancel</button>
          <div class="cf-add-status" style="font-size:0.75rem;color:#888;margin-top:4px;text-align:center;"></div>
        </div>
      </div>
    `;

    card._postData = post;

    card.querySelector('.cf-action-customize').addEventListener('click', (e) => {
      e.stopPropagation();
      loadDesign(post);
    });

    card.querySelector('.cf-action-cart').addEventListener('click', (e) => {
      e.stopPropagation();
      togglePicker(card, true);
      // Default to the design's original ring size if present
      if (post.ringSize) {
        const sel = card.querySelector('.cf-size-select');
        const target = String(post.ringSize);
        if (Array.from(sel.options).some(o => o.value === target)) sel.value = target;
      }
    });

    card.querySelector('.cf-cancel-add').addEventListener('click', (e) => {
      e.stopPropagation();
      togglePicker(card, false);
    });

    card.querySelector('.cf-confirm-add').addEventListener('click', async (e) => {
      e.stopPropagation();
      const sel = card.querySelector('.cf-size-select');
      const statusEl = card.querySelector('.cf-add-status');
      await addPopularToCart(post, sel.value, statusEl, card);
    });

    return card;
  }

  function togglePicker(card, show) {
    const picker = card.querySelector('.cf-size-picker');
    const actions = card.querySelector('.cf-card-actions');
    if (picker) picker.style.display = show ? 'block' : 'none';
    if (actions) actions.style.display = show ? 'none' : 'flex';
  }

  // Fetch full post data (designHash isn't in the index summary) for a given id.
  async function fetchFullPost(post) {
    if (post.designHash) return post;
    if (!post.id) return null;
    try {
      const res = await fetch(`${feedUrl}?id=${encodeURIComponent(post.id)}`);
      const data = await res.json();
      return data.post || null;
    } catch { return null; }
  }

  async function loadDesign(post) {
    const full = await fetchFullPost(post);
    if (!full || !full.designHash) return;

    loadedCommunityPostId = post.id;

    const designer = document.querySelector('[data-ring-designer]');
    if (designer) designer.scrollIntoView({ behavior: 'smooth' });

    const rd = window._rdInstance;
    if (rd && typeof rd.loadDesignFromHash === 'function') {
      rd.loadDesignFromHash(full.designHash);
    } else {
      window.location.hash = full.designHash;
      window.location.reload();
    }
  }

  // Add the post's design to cart at the chosen ring size, using the EXACT same
  // product + variant map as the regular designer flow.
  async function addPopularToCart(post, sizeStr, statusEl, card) {
    const setStatus = (txt, color = '#888') => {
      if (statusEl) { statusEl.textContent = txt; statusEl.style.color = color; }
    };

    const confirmBtn = card.querySelector('.cf-confirm-add');
    confirmBtn.disabled = true;
    setStatus('Loading design…');

    const full = await fetchFullPost(post);
    if (!full || !full.designHash) {
      setStatus('Could not load design data', '#c44');
      confirmBtn.disabled = false;
      return;
    }

    // Variant routing strategy (priority order):
    //   1. pricesBySize[size].variantId — pre-computed at admin save: matches the
    //      ACTUAL tier this design occupies at this size (handles size-dependent area).
    //   2. variantMap[storedTier][size] — legacy path using the design's original tier
    //      (incorrect for designs whose area changes with size, but used when
    //      pricesBySize is absent on older records).
    //   3. window._rdProductVariantId — last-resort default variant.
    let variantId = null;
    let tierForProperty = '';

    const pbs = full.pricesBySize || post.pricesBySize;
    if (pbs && typeof pbs === 'object' && pbs[sizeStr] && pbs[sizeStr].variantId) {
      variantId = pbs[sizeStr].variantId;
      tierForProperty = pbs[sizeStr].tier || pbs[sizeStr].tierSlug || '';
    }
    // Width routing: _rdVariantMap and the default variant are the 8mm base
    // product ONLY — non-8mm designs must resolve via the width-keyed server map.
    const cardWidth = String(full.bandWidth || post.bandWidth || 8);
    if (!variantId && cardWidth === '8') {
      const variantMap = window._rdVariantMap;
      const tierRaw = String(full.tier || '').trim();
      const tierSlug = tierRaw.toLowerCase().replace(/^t/, 't');
      if (variantMap && tierSlug && variantMap[tierSlug]) {
        variantId = variantMap[tierSlug][sizeStr];
      }
      tierForProperty = tierRaw || tierSlug;
    }
    if (!variantId) {
      try {
        if (!window._rdServerVariantMap) {
          window._rdServerVariantMap = await fetch(feedUrl.replace('/community', '/variant-map'))
            .then(r => r.ok ? r.json() : null).catch(() => null);
        }
        const sm = window._rdServerVariantMap;
        const wt = sm && ((sm.widths && sm.widths[cardWidth]) || (cardWidth === '8' ? sm.tiers : null));
        const ts2 = String(full.tier || '').trim().toLowerCase();
        const sv = wt && wt[ts2] && (wt[ts2][sizeStr] || wt[ts2][String(sizeStr).replace('.0', '')]);
        if (sv) { variantId = sv.id; tierForProperty = tierForProperty || full.tier || ''; }
      } catch (e) { /* fall through */ }
    }
    if (!variantId && cardWidth === '8') variantId = window._rdProductVariantId;
    if (!variantId) {
      setStatus('Variant lookup failed — please open in designer instead', '#c44');
      confirmBtn.disabled = false;
      return;
    }

    setStatus('Adding to cart…');

    // Reconstruct the design URL with the chosen ring size so admin-side preview reflects it.
    // We swap the sz= param in the design hash.
    let designHash = full.designHash;
    try {
      const params = new URLSearchParams(designHash.replace(/^#/, ''));
      params.set('sz', sizeStr);
      designHash = '#' + params.toString();
    } catch {}

    // Rewrite the AUTHORED size in the hash to the customer's chosen size so
    // admin "Edit in Designer" opens the order at the ordered size (the Size
    // property remains the fulfillment-authoritative value either way).
    let orderedHash = designHash;
    try {
      const shp = new URLSearchParams(String(designHash).replace(/^#/, ''));
      shp.set('sz', sizeStr);
      orderedHash = '#' + shp.toString();
    } catch (e) { /* keep authored hash */ }
    const designURL = window.location.origin + window.location.pathname + orderedHash;

    // Save the SVG/design-key path — if the design has been engraved through the normal
    // designer flow, the blob already exists at full.designKey or similar. If not, we
    // generate a fresh design-key and POST to /design-svgs. For Popular Designs we
    // typically don't have a pre-stored SVG (popular records carry the hash, not the SVG),
    // so we'll skip the SVG-save step here — admin can regenerate from the URL hash later
    // via the existing recovery flow.

    const properties = {
      '_ring_designer':  '1',
      'Material':        'Stainless Steel',
      'Width':           `${full.bandWidth || 8}mm`,
      'Size':            sizeStr,
      '_design_url':     designURL,
      '_design_tier':    tierForProperty || full.tier || '',
      '_popular_id':     post.id,
      // Customer-facing fields so the cart drawer + order detail show the design name and image.
      'Design Name':     full.designName || post.designName || 'Popular Design',
    };
    // Carry the thumbnail through as 'Design Preview' so the cart renders it (cart Liquid
    // templates render the property named exactly "Design Preview" as an image).
    if (full.thumbnail) {
      properties['Design Preview'] = full.thumbnail;
    }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1, properties }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || `cart/add returned ${res.status}`);
      }
      setStatus('Added to cart!', '#2a7');
      // Track the add-to-cart for long-term popularity metrics.
      loadedCommunityPostId = post.id;
      trackCartAdd();
      // Redirect to cart after a brief delay
      setTimeout(() => { window.location.href = '/cart'; }, 900);
    } catch (e) {
      console.warn('[PopularDesigns] Add to cart failed:', e);
      setStatus('Failed: ' + (e.message || 'unknown'), '#c44');
      confirmBtn.disabled = false;
    }
  }

  // Track which popular design is currently loaded for cart-add attribution
  let loadedCommunityPostId = null;

  function trackCartAdd() {
    if (!loadedCommunityPostId || !feedUrl) return;
    fetch(`${feedUrl}?id=${loadedCommunityPostId}&action=cartAdd`, { method: 'PUT' }).catch(() => {});
    loadedCommunityPostId = null;
  }

  function escHtml(str) {
    const d = document.createElement('div');
    d.textContent = str || '';
    return d.innerHTML;
  }

  // Format cents → "$26" (drops trailing .00 for clean display).
  function formatCents(cents) {
    if (cents == null) return '';
    const dollars = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return `$${dollars}`;
  }

  // Look up a flat tier price from the global pricing table exposed by the designer
  // snippet. Used as a FALLBACK when a post predates pricesBySize.
  function formatTierPrice(tier) {
    if (!tier) return '';
    const tiers = window._rdPricingTiers;
    if (!Array.isArray(tiers)) return '';
    const slug = String(tier).toLowerCase().replace(/^t/, 't');
    const entry = tiers.find(t => t.tier === slug);
    if (!entry) return '';
    if (entry.price == null) return 'Quote on request';
    return formatCents(entry.price);
  }

  // Sizes a customer can actually buy this design at. Empty array = pre-pricesBySize
  // record (legacy) and the caller should fall back to all sizes.
  function getAvailableSizes(post) {
    const pbs = post.pricesBySize;
    if (!pbs || typeof pbs !== 'object') return [];
    return RING_SIZES.filter(s => {
      const row = pbs[s];
      return row && row.available !== false && row.variantId;
    });
  }

  // The card price label. Three cases:
  //   1. pricesBySize present → range ("$28–$42") or single ("$32") across available sizes
  //   2. pricesBySize absent (legacy) → flat tier price from window._rdPricingTiers
  //   3. neither resolvable → ''
  function formatCardPriceLabel(post) {
    const pbs = post.pricesBySize;
    if (pbs && typeof pbs === 'object') {
      const prices = RING_SIZES
        .map(s => pbs[s])
        .filter(r => r && r.available !== false && typeof r.price === 'number')
        .map(r => r.price);
      if (prices.length === 0) return formatTierPrice(post.tier); // pbs there but unusable
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      if (min === max) return formatCents(min);
      return `${formatCents(min)}–${formatCents(max)}`;
    }
    return formatTierPrice(post.tier);
  }

  // Per-size price suffix shown in the picker dropdown. Empty if pricesBySize absent.
  function formatSizePrice(post, size) {
    const pbs = post.pricesBySize;
    if (!pbs || typeof pbs !== 'object') return '';
    const row = pbs[size];
    if (!row || typeof row.price !== 'number') return '';
    return formatCents(row.price);
  }

  // Public API. handleSort is kept as a no-op so any leftover onclick references don't error.
  // ───────────────────────────────────────────────────────────────────────
  //  Placement support — admin-configured widgets that can be dropped into
  //  any Shopify section via the popular-designs-placement.liquid section.
  //  Reads placement config + designs from the community endpoint and renders
  //  in scroll (with arrows / auto-scroll) or grid layout per config.
  // ───────────────────────────────────────────────────────────────────────
  async function initPlacement(containerId) {
    const container = document.getElementById(containerId);
    if (!container) return;
    const placementId = container.getAttribute('data-placement-id');
    const url = container.getAttribute('data-feed-url') || feedUrl ||
                'https://brs-ring-designer.netlify.app/.netlify/functions/community';
    if (!placementId) return;
    // Make sure variant map etc are exposed so cart-add works from this section
    // independently of whether the main designer is rendered on the same page.
    // (community-feed assumes window._rdVariantMap is set; if it isn't, cart-add
    // falls back to the default variant — acceptable for placements that live on
    // pages without the designer.)
    if (!feedUrl) feedUrl = url;

    const loadingEl = container.querySelector('.cf-placement-loading');
    if (loadingEl) loadingEl.style.display = 'block';

    try {
      const res = await fetch(`${url}?placement=${encodeURIComponent(placementId)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const placement = data.placement || {};
      const posts = Array.isArray(data.posts) ? data.posts : [];

      if (loadingEl) loadingEl.style.display = 'none';
      if (posts.length === 0) {
        container.innerHTML = `<div style="text-align:center;color:#888;font-size:0.85rem;padding:24px 0;">No designs in this placement yet.</div>`;
        return;
      }

      renderPlacement(container, placement, posts);
    } catch (e) {
      console.warn('[PopularDesigns] Placement load failed:', e);
      if (loadingEl) loadingEl.textContent = 'Failed to load placement.';
    }
  }

  function renderPlacement(container, placement, posts) {
    const layout = placement.layout === 'grid' ? 'grid' : 'scroll';
    const cardWidth = parseInt(placement.cardWidth) || 280;
    const cardsVisible = parseInt(placement.cardsVisible) || 4;
    const showArrows = placement.showArrows !== false;
    const autoScroll = placement.autoScroll === true;
    const title = placement.title || '';

    // Build the inner structure.
    container.innerHTML = '';

    if (title) {
      const h = document.createElement('h3');
      h.className = 'cf-placement-title';
      h.textContent = title;
      h.style.cssText = 'margin:0 0 12px;font-size:1.15rem;font-weight:600;';
      container.appendChild(h);
    }

    const stripWrap = document.createElement('div');
    stripWrap.className = 'cf-placement-strip-wrap';
    stripWrap.style.position = 'relative';

    const grid = document.createElement('div');
    grid.className = 'cf-grid';
    grid.setAttribute('data-layout', layout);
    if (layout === 'scroll') {
      // CSS uses CSS vars so the inline card width can be picked up by ::before-style children.
      grid.style.setProperty('--cf-card-width', `${cardWidth}px`);
      grid.style.setProperty('--cf-cards-visible', String(cardsVisible));
    } else {
      // Grid layout — use cardsVisible to set columns and cardWidth as min-width.
      grid.style.setProperty('--cf-card-width', `${cardWidth}px`);
      grid.style.gridTemplateColumns = `repeat(auto-fill, minmax(${cardWidth}px, 1fr))`;
    }
    stripWrap.appendChild(grid);

    posts.forEach(post => {
      const card = createCard(post);
      if (layout === 'scroll') {
        card.style.flex = `0 0 ${cardWidth}px`;
      }
      grid.appendChild(card);
    });

    container.appendChild(stripWrap);

    // Arrow buttons (scroll layout only, no auto-scroll).
    if (layout === 'scroll' && showArrows && !autoScroll) {
      const leftBtn = document.createElement('button');
      leftBtn.className = 'cf-scroll-arrow cf-scroll-arrow-left';
      leftBtn.innerHTML = '‹';
      leftBtn.setAttribute('aria-label', 'Scroll left');
      leftBtn.addEventListener('click', () => scrollByCardWidth(grid, -1, cardWidth));
      stripWrap.appendChild(leftBtn);

      const rightBtn = document.createElement('button');
      rightBtn.className = 'cf-scroll-arrow cf-scroll-arrow-right';
      rightBtn.innerHTML = '›';
      rightBtn.setAttribute('aria-label', 'Scroll right');
      rightBtn.addEventListener('click', () => scrollByCardWidth(grid, +1, cardWidth));
      stripWrap.appendChild(rightBtn);
    }

    // Auto-scroll marquee.
    if (layout === 'scroll' && autoScroll) {
      startMarquee(grid);
    }
  }

  function scrollByCardWidth(grid, direction, cardWidth) {
    const gap = 16; // matches CSS gap
    grid.scrollBy({ left: (cardWidth + gap) * direction, behavior: 'smooth' });
  }

  // Continuous slow horizontal scroll. Pauses on hover/touch and on card interaction.
  function startMarquee(grid) {
    const PX_PER_SEC = 30;
    let paused = false;
    let last = performance.now();
    let raf;

    const pause = () => { paused = true; };
    const resume = () => { paused = false; last = performance.now(); };

    grid.addEventListener('mouseenter', pause);
    grid.addEventListener('mouseleave', resume);
    grid.addEventListener('touchstart', pause, { passive: true });
    grid.addEventListener('touchend', resume, { passive: true });
    // Pause when the user interacts with any control inside a card (Choose Size, etc.).
    grid.addEventListener('click', () => {
      pause();
      // Auto-resume after a few seconds of inactivity so the marquee doesn't stall forever.
      clearTimeout(grid._marqueeResume);
      grid._marqueeResume = setTimeout(resume, 8000);
    });

    function tick(now) {
      const dt = Math.min(50, now - last) / 1000;
      last = now;
      if (!paused) {
        grid.scrollLeft += PX_PER_SEC * dt;
        // Loop back when we run out of content. This is a simple loop; for true
        // infinite marquee we'd clone cards, but for 6-12 designs this is fine.
        const max = grid.scrollWidth - grid.clientWidth;
        if (grid.scrollLeft >= max - 1) grid.scrollLeft = 0;
      }
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
  }

  window.CommunityFeed = { init, initPlacement, trackCartAdd, sort: function() {} };

  document.addEventListener('DOMContentLoaded', () => {
    const btn = document.querySelector('.cf-load-more');
    if (btn) {
      btn.addEventListener('click', () => {
        const container = document.getElementById('community-feed');
        if (container && hasMore) loadFeed(container);
      });
    }
  });
})();
